/**
 * Volume API service.
 * Provides operations for volumes.
 *
 * API Endpoints:
 * - GET /volumes - List all volumes
 * - GET /volumes/:name - Get volume by name
 * - PUT /volumes/:name - Create a volume
 * - DELETE /volumes/:name - Delete a volume
 *
 * @module services/volumeService
 */

import { httpClient } from './httpClient';
import type { VolumeResponse } from '@/types';

const ENDPOINT = '/volumes';

/**
 * Volume Service
 * Encapsulates all volume API operations
 */
export const volumeService = {
  /**
   * List volumes with pagination
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<VolumeResponse[]> - Array of volumes
   */
  async list(page: number = 1, pageSize: number = 25): Promise<VolumeResponse[]> {
    console.log('[VolumeService] Fetching volumes - Page:', page, 'PageSize:', pageSize);
    try {
      const response = await httpClient.get<VolumeResponse[]>(
        `${ENDPOINT}?page=${page}&pageSize=${pageSize}`
      );
      console.log('[VolumeService] Fetched', response.data.length, 'volumes');
      return response.data;
    } catch (error) {
      console.error('[VolumeService] Failed to fetch volumes:', error);
      throw error;
    }
  },

  /**
   * Get a volume by name
   * @param name - The name of the volume
   * @returns Promise<VolumeResponse> - The volume
   */
  async getByName(name: string): Promise<VolumeResponse> {
    console.log('[VolumeService] Fetching volume:', name);
    const encodedName = encodeURIComponent(name);
    try {
      const response = await httpClient.get<VolumeResponse>(`${ENDPOINT}/${encodedName}`);
      console.log('[VolumeService] Fetched volume:', response.data.volumeName);
      return response.data;
    } catch (error) {
      console.error('[VolumeService] Failed to fetch volume:', name, error);
      throw error;
    }
  },

  /**
   * Create a volume
   * @param name - The name of the volume
   * @param request - The volume creation request
   * @returns Promise<VolumeResponse> - The created volume
   */
  async create(
    name: string,
    request: { type: string; size?: string; labels?: Record<string, string> }
  ): Promise<VolumeResponse> {
    console.log('[VolumeService] Creating volume:', name);
    const encodedName = encodeURIComponent(name);
    try {
      const response = await httpClient.put<VolumeResponse>(`${ENDPOINT}/${encodedName}`, request);
      console.log('[VolumeService] Created volume:', response.data.volumeName);
      return response.data;
    } catch (error) {
      console.error('[VolumeService] Failed to create volume:', name, error);
      throw error;
    }
  },

  /**
   * Delete a volume by name
   * @param name - The name of the volume to delete
   * @returns Promise<void>
   */
  async delete(name: string): Promise<void> {
    console.log('[VolumeService] Deleting volume:', name);
    const encodedName = encodeURIComponent(name);
    try {
      await httpClient.delete(`${ENDPOINT}/${encodedName}`);
      console.log('[VolumeService] Deleted volume:', name);
    } catch (error) {
      console.error('[VolumeService] Failed to delete volume:', name, error);
      throw error;
    }
  },

  async fork(
    sourceVolumeName: string,
    request: { destinationVolumeName: string; labels?: Record<string, string> }
  ): Promise<VolumeResponse> {
    console.log(
      '[VolumeService] Forking volume:',
      sourceVolumeName,
      '→',
      request.destinationVolumeName
    );
    const encodedName = encodeURIComponent(sourceVolumeName);
    try {
      const response = await httpClient.post<VolumeResponse>(
        `${ENDPOINT}/${encodedName}/fork`,
        request
      );
      console.log('[VolumeService] Forked volume:', response.data.volumeName);
      return response.data;
    } catch (error) {
      console.error('[VolumeService] Failed to fork volume:', sourceVolumeName, error);
      throw error;
    }
  },
};
